import type { WholeStoryType } from "@config";
import { useCallback } from "react";
import uuid from "react-uuid";
import { track } from "@/features/analytics";
import { captureSentryError } from "@/shared/utils/sentry-helpers";
import { hasUnpublishedContent } from "../rendering-provider/utils/has-unpublished-content";
import { useGenerateCanonical } from "./use-generate-canonical";

export const VERSION_TEMPLATE_TIMEOUT_MS = 30_000;

type PageWithContent = {
  _id?: string;
  orgId?: string;
  layoutVersion?: string | null;
  settings?: Record<string, unknown> | null;
} | null;

type VersionTemplateCanonical = {
  canonicalId: string;
  contentPointer: string;
};

/**
 * Picks the story template a newly created version must be baked from.
 *
 * The backend renders every version out of a template object in the stories
 * bucket — `<orgId>/<settings.canonical>` — and that object is rewritten by
 * exactly one code path: `usePublish`, via `useGenerateCanonical`. Autosave
 * never touches it. So a story that was edited but not re-published hands every
 * version created afterwards its PRE-EDIT content, even though the editor, the
 * share dialog and the story preview all show the new content (they read
 * `settings.contentPointer`). That is Monday #13007835807: a pricing story was
 * updated, versions were created from the Salesforce panel — which has no
 * publish step at all — and every one of them rendered the old prices.
 *
 * The fix renders the story's CURRENT saved content into a throwaway template
 * object and passes its id as `duplicateFromCanonicalId`, which
 * `pages/service.js:createVersion` already honours as the template key. The
 * story's own canonical is deliberately left untouched: the master story's live
 * link must keep serving the last PUBLISHED content, so this never leaks a
 * draft into anything but the version the user explicitly asked for.
 *
 * Returns `{ canonicalId, contentPointer }` for the throwaway template so
 * callers can persist the pointer that was actually rendered without rereading
 * `page.settings` after the await. Returns `null` when the story is already
 * in sync (the overwhelmingly common case — the in-app Share button forces an
 * "Update & Share" first) and when anything goes wrong, so the caller falls
 * back to today's behaviour rather than failing the create.
 */
export const useVersionTemplateCanonical = () => {
  const { generateCanonical } = useGenerateCanonical();

  const resolveVersionCanonicalId = useCallback(
    async (page: PageWithContent): Promise<VersionTemplateCanonical | null> => {
      if (!hasUnpublishedContent(page)) return null;

      const settings = page?.settings as { contentPointer: string };
      const contentPointer = settings.contentPointer;
      const controller = new AbortController();
      let timeoutId: ReturnType<typeof setTimeout> | undefined;

      // The abort signal reaches the pointer fetch and the GCS upload, but NOT
      // the `getUploadFileToken` POST inside generateCanonical — that is a plain
      // axios call with no `timeout` and no signal (src/lib/axios.ts), so a
      // stalled backend would leave this promise pending forever and hang the
      // create with no fallback. Racing the whole resolution against the
      // deadline is what actually makes VERSION_TEMPLATE_TIMEOUT_MS a bound.
      const deadline = new Promise<never>((_resolve, reject) => {
        timeoutId = setTimeout(() => {
          controller.abort();
          reject(new Error("version template render timed out"));
        }, VERSION_TEMPLATE_TIMEOUT_MS);
      });

      const render = async (): Promise<VersionTemplateCanonical> => {
        const response = await fetch(contentPointer, {
          signal: controller.signal,
        });
        if (!response.ok)
          throw new Error(`content pointer responded ${response.status}`);
        const content = await response.json();
        const slides = content?.slides;
        // An empty or slide-less blob would bake a blank deck — strictly worse
        // than the stale-but-complete published template.
        if (!Array.isArray(slides) || slides.length === 0)
          throw new Error("content pointer carries no slides");

        const canonicalId = uuid();
        await generateCanonical(
          { ...page, slides } as unknown as WholeStoryType,
          canonicalId,
          { signal: controller.signal },
        );
        track("version rendered from unpublished content", {
          storyId: page?._id,
        });
        return { canonicalId, contentPointer };
      };

      try {
        const renderPromise = render();
        // The loser of the race still settles. Without a handler its abort
        // rejection surfaces as an unhandled rejection; the race keeps seeing
        // the original promise, so this only swallows the post-deadline echo.
        renderPromise.catch(() => {});
        return await Promise.race([renderPromise, deadline]);
      } catch (error) {
        track("failed to render version template", { error });
        captureSentryError(
          error instanceof Error ? error : new Error(String(error)),
          {
            tags: { feature: "publish", operation: "versionTemplateCanonical" },
            extra: { storyId: page?._id, contentPointer },
            fingerprint: ["publish", "version-template-canonical"],
          },
        );
        return null;
      } finally {
        clearTimeout(timeoutId);
      }
    },
    [generateCanonical],
  );

  return { resolveVersionCanonicalId };
};
